import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { Button } from 'Components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'Components/ui/popover';
import { cn } from 'lib/utils';
import { DEFAULT_TIME, timeLabel, to12h, to24h, wrap } from './scheduleView';

// A clock face: the hour and the minute, typed or stepped, and AM or PM.
// The value is "HH:mm" (24-hour), the same the schedule carries.

const pad = (n) => String(n).padStart(2, '0');
const QUICK_MINUTES = [0, 15, 30, 45];

const STEP = 'flex h-7 w-14 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';
const CHIP = 'appearance-none rounded-full border-[1px] border-solid px-3 py-1 text-sm transition-colors';

/** One number of the clock: a box to type in, with a step up and a step down. */
const Dial = ({ label, text, onText, onCommit, onStep }) => (
  <div className='flex flex-col items-center'>
    <button type='button' className={STEP} aria-label={`${label} up`} onClick={() => onStep(1)}>
      <ChevronUp size={16} />
    </button>
    <input
      aria-label={label}
      inputMode='numeric'
      maxLength={2}
      value={text}
      onChange={(event) => onText(event.target.value.replace(/\D/g, ''))}
      onBlur={onCommit}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();
          onStep(event.key === 'ArrowUp' ? 1 : -1);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onCommit();
        }
      }}
      className='h-12 w-14 rounded-lg border-[1px] border-solid border-input bg-white text-center text-2xl font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
    />
    <button type='button' className={STEP} aria-label={`${label} down`} onClick={() => onStep(-1)}>
      <ChevronDown size={16} />
    </button>
  </div>
);

const TimePicker = ({ value, onChange, disabled, allowed = true }) => {
  const [open, setOpen] = useState(false);
  const { hour, minute, period } = to12h(value || DEFAULT_TIME);
  const [hourText, setHourText] = useState(String(hour));
  const [minuteText, setMinuteText] = useState(pad(minute));

  // The boxes follow the value, except while the user is typing in them.
  useEffect(() => {
    setHourText(String(hour));
    setMinuteText(pad(minute));
  }, [hour, minute]);

  const set = (h, m, p) => onChange(to24h(h, m, p));

  const commitHour = () => {
    const n = parseInt(hourText, 10);
    if (n >= 1 && n <= 12) set(n, minute, period);
    else setHourText(String(hour));
  };
  const commitMinute = () => {
    const n = parseInt(minuteText, 10);
    if (n >= 0 && n <= 59) set(hour, n, period);
    else setMinuteText(pad(minute));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          disabled={disabled}
          className={cn('h-10 justify-start bg-white font-normal shadow-none', !value && 'text-muted-foreground')}
        >
          <Clock size={16} className='mr-2 text-muted-foreground' />
          {value ? timeLabel(value) : 'Pick a time'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-auto p-4'>
        <div className='flex items-center gap-2'>
          <Dial
            label='Hour'
            text={hourText}
            onText={setHourText}
            onCommit={commitHour}
            onStep={(d) => set(wrap(hour + d, 1, 12), minute, period)}
          />
          <span className='pb-0.5 text-2xl font-medium text-foreground'>:</span>
          <Dial
            label='Minute'
            text={minuteText}
            onText={setMinuteText}
            onCommit={commitMinute}
            onStep={(d) => set(hour, wrap(minute + d, 0, 59), period)}
          />
          <div className='ml-2 flex flex-col gap-1' role='radiogroup' aria-label='AM or PM'>
            {['AM', 'PM'].map((p) => (
              <button
                key={p}
                type='button'
                role='radio'
                aria-checked={period === p}
                onClick={() => set(hour, minute, p)}
                className={cn(
                  'appearance-none rounded-md border-[1px] border-solid px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-white text-muted-foreground hover:text-foreground',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className='mt-3 flex gap-1.5'>
          {QUICK_MINUTES.map((m) => (
            <button
              key={m}
              type='button'
              onClick={() => set(hour, m, period)}
              className={cn(
                CHIP,
                minute === m
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-white text-foreground/80 hover:border-foreground/40 hover:text-foreground',
              )}
            >
              :{pad(m)}
            </button>
          ))}
        </div>

        {!allowed && <p className='mt-3 text-xs text-destructive'>This time has already passed today.</p>}

        <div className='mt-3 flex justify-end'>
          <Button type='button' size='sm' variant='outline' className='shadow-none' onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TimePicker;
